import { Component, type ErrorInfo, type ReactNode } from "react";
import { isAppUpdateRequiredError, isChunkLoadFailure } from "../lib/chunk-recovery";

/**
 * The boundary around the route content.
 *
 * ═══ Why this application needed one ══════════════════════════════════════════
 *
 * It had no error boundary at all. With a single bundle that was survivable — if the bundle loaded,
 * every screen was present. Route splitting changed that: each screen is now fetched on demand, so a
 * routine deploy can make the next click reject, and an uncaught render throw unmounts the tree to the
 * nearest boundary. With none, that is a blank white page.
 *
 * ═══ The mistake this deliberately does not repeat ════════════════════════════
 *
 * The AI console's boundary records what happened when a boundary printed `error.message`: a customer
 * read "Failed to fetch dynamically imported module" followed by an asset filename. That string names
 * our build output, gives the reader nothing they can act on, and makes a normal deploy look like a
 * crash.
 *
 * So the technical detail is logged to the console for whoever is debugging, and never rendered.
 *
 * ═══ Two outcomes, because they are genuinely different ═══════════════════════
 *
 *   · A stale build is not really an error — it is "we shipped while you were reading". It gets a calm
 *     notice and one button that fixes it, in the product's own voice.
 *   · Anything else is a fault. It says so plainly, without pretending a refresh is a fix, and offers
 *     a way back to a screen that works.
 *
 * ═══ Why it wraps the route and not the whole app ═════════════════════════════
 *
 * Placed inside AppShell, around the Suspense. A failure in one screen leaves the rail, the header and
 * the workspace context on screen, so the person can navigate somewhere else — which is almost always
 * what they want, and is impossible if the boundary has replaced the entire window. `key`ed on the
 * location by the caller so navigating away RESETS it; without that, one broken screen would poison
 * every subsequent route because a boundary that has caught stays caught.
 */

type Kind = "update" | "fault";

interface Props {
  children: ReactNode;
  /** Where to send someone whose current screen is broken. */
  onGoHome?: () => void;
}

interface State {
  kind: Kind | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { kind: null };

  static getDerivedStateFromError(err: unknown): State {
    // A missing chunk and a tagged update failure are the same story to the reader.
    const isUpdate = isAppUpdateRequiredError(err) || isChunkLoadFailure(err);
    return { kind: isUpdate ? "update" : "fault" };
  }

  override componentDidCatch(err: unknown, info: ErrorInfo): void {
    /*
     * To the console, not to the screen. This is the half that was missing from the AI console's old
     * boundaries: the customer saw the technical detail and we saw nothing, which is exactly backwards.
     */
    // eslint-disable-next-line no-console
    console.error("[email-console] route error", err, info.componentStack);
  }

  private reload = () => window.location.reload();

  override render(): ReactNode {
    const { kind } = this.state;
    if (!kind) return this.props.children;

    const isUpdate = kind === "update";

    return (
      <div
        role="alert"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          maxWidth: 560,
        }}
      >
        <h2 style={{ fontSize: "var(--text-section)", fontWeight: 600, color: "var(--text)" }}>
          {isUpdate ? "This console has been updated" : "This screen could not be displayed"}
        </h2>
        <p style={{ marginTop: 8, fontSize: "var(--text-body)", color: "var(--text-2)", lineHeight: 1.55 }}>
          {isUpdate
            ? "A newer version was released while this page was open, so part of it could not be loaded. Reloading picks up the new version. Nothing has been lost."
            : "Something in this screen failed while rendering. Your data is unaffected, and the rest of the console still works."}
        </p>
        <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={this.reload}
            style={{
              height: 32, padding: "0 14px", borderRadius: "var(--radius)",
              background: "var(--accent)", color: "#fff", border: "none",
              fontSize: "var(--text-body)", fontWeight: 500, cursor: "pointer",
            }}
          >
            {isUpdate ? "Reload" : "Reload the console"}
          </button>
          {/* Only offered for a fault: on a stale build every other screen is equally stale. */}
          {!isUpdate && this.props.onGoHome && (
            <button
              type="button"
              onClick={() => { this.setState({ kind: null }); this.props.onGoHome?.(); }}
              style={{
                height: 32, padding: "0 14px", borderRadius: "var(--radius)",
                background: "var(--surface)", color: "var(--text)",
                border: "1px solid var(--border)", fontSize: "var(--text-body)", cursor: "pointer",
              }}
            >
              Go to Overview
            </button>
          )}
        </div>
      </div>
    );
  }
}
